/**
 * Auth utility functions — mirrors Portal-Ui's authUtils.js.
 *
 * Provides helpers for reading auth state from localStorage and
 * normalizing roles across both legacy and SSO auth paths.
 *
 * NOTE: This file deliberately avoids importing from AuthContext to prevent
 * circular dependencies (AuthContext imports normalizeRoles from here).
 */

// ---------------------------------------------------------------------------
// Local type aliases (mirrors AuthContext types without importing them)
// ---------------------------------------------------------------------------

interface StoredLegacyAuth {
  bearerToken: string;
  refreshTokenId: string;
  expiresAtUtc: string | null;
  identity: {
    roles?: string[] | string;
    [key: string]: unknown;
  };
}

interface StoredSsoSession {
  claims: {
    cus_roles?: string[] | string;
    [key: string]: unknown;
  };
}

// ---------------------------------------------------------------------------
// Role normalization
// ---------------------------------------------------------------------------

/**
 * Normalize a roles value into a string array.
 * Handles: string[], single string, null/undefined.
 */
export function normalizeRoles(rolesRaw: unknown): string[] {
  if (Array.isArray(rolesRaw)) {
    return rolesRaw as string[];
  }
  if (rolesRaw != null && typeof rolesRaw === 'string') {
    return [rolesRaw];
  }
  return [];
}

// ---------------------------------------------------------------------------
// Storage helpers
// ---------------------------------------------------------------------------

/**
 * Read the legacy auth object from localStorage.
 * Returns null if missing or unparseable.
 */
export function getAuthFromStorage(): StoredLegacyAuth | null {
  try {
    const stored = localStorage.getItem('auth');
    return stored ? (JSON.parse(stored) as StoredLegacyAuth) : null;
  } catch {
    return null;
  }
}

/**
 * Read the SSO session object from localStorage.
 * Returns null if missing or unparseable.
 */
export function getSsoSessionFromStorage(): StoredSsoSession | null {
  try {
    const stored = localStorage.getItem('ssoSession');
    return stored ? (JSON.parse(stored) as StoredSsoSession) : null;
  } catch {
    return null;
  }
}

/**
 * Get the current user's roles from whichever auth source is present.
 * Checks legacy auth first, then SSO session.
 */
export function getRolesFromStorage(): string[] {
  const auth = getAuthFromStorage();
  if (auth?.identity?.roles) {
    return normalizeRoles(auth.identity.roles);
  }

  const ssoSession = getSsoSessionFromStorage();
  if (ssoSession?.claims?.cus_roles) {
    return normalizeRoles(ssoSession.claims.cus_roles);
  }

  return [];
}

// ---------------------------------------------------------------------------
// Role checks
// ---------------------------------------------------------------------------

/**
 * Check if the current user has a specific role.
 */
export function hasRole(role: string): boolean {
  if (!role) return false;
  const roles = getRolesFromStorage();
  return roles.includes(role);
}

/**
 * Check if the current user has any of the required roles.
 * Returns true if requiredRoles is empty/undefined.
 */
export function hasAnyRole(requiredRoles: string[] = []): boolean {
  if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) return true;
  const roles = getRolesFromStorage();
  return requiredRoles.some((r) => roles.includes(r));
}
